var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * CZNA-494 — the PACKAGED default form-config template for a record type, i.e.
 * `mod-czna-authorities/src/main/resources/form-configs/{type}.json` as shipped
 * by the running JAR.
 *
 * Why this and not something more obvious:
 *
 *  - The **effective** config (`GET /effective/{type}`) is compiled from the
 *    STORED template, so on an environment where NK has already saved a
 *    stripped-down form it is missing exactly the definitions we want to
 *    recover (that is the CZNA-450 situation this ticket came out of). It also
 *    carries compiled labels, which must not be written back into a raw
 *    template — see `newFieldTemplate.ts`.
 *  - The FE has no bundled copy of `form-configs/*.json` on purpose (the old
 *    build-time byte-copy of the MARC catalog drifted and was deleted).
 *  - So the packaged document is read from the one endpoint that returns it:
 *    `GET /{key}/default-drift`, whose `packagedDefault` is the packaged raw
 *    JSON verbatim (`ModuleSettingServiceImpl.getFormConfigDriftDiff`).
 *
 * It always asks for the record type's `default` template, never for the
 * template currently open in the editor: a template NK created has no packaged
 * default at all (the endpoint 409s), and the pristine repo definition of a tag
 * is what the admin wants to inherit either way.
 *
 * The key is split on the FIRST dot only — `form_config.{TYPE}.{templateId}`
 * has three segments and parsing it as two produced a real bug once already
 * (CZNA-456, see `moduleSettingInvalidationKeys`).
 *
 * Failure is not surfaced. This is an advisory enrichment of the add-field
 * dialog; without it the dialog falls back to the MARC catalog and the admin
 * sees one option instead of several, which is the pre-CZNA-494 behaviour.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference. It is
 * closure-captured and NEVER listed in a dependency array.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { CZNA_MODULE_SETTING_DEFAULT_DRIFT } from '../types/apiPaths';
import { formConfigSettingKey } from '../types/api';
var FORM_CONFIG_KEY_PREFIX = 'form_config.';
/**
 * The record type inside a `form_config.{TYPE}.{templateId}` key, or `null` for
 * any other setting key (the visual editor is only ever pointed at form
 * configs, but the hook must not invent a type for `import_defaults.*`).
 */
export var recordTypeOfFormConfigKey = function (settingKey) {
    if (!settingKey.startsWith(FORM_CONFIG_KEY_PREFIX))
        return null;
    var rest = settingKey.substring(FORM_CONFIG_KEY_PREFIX.length);
    var dot = rest.indexOf('.');
    var type = dot === -1 ? rest : rest.substring(0, dot);
    return type || null;
};
/** Cache slot. Deliberately NOT `formConfigDriftDiffQueryKey` — that one is
 *  `cacheTime: 0` because the compare dialog needs a fresh `optimisticLock`,
 *  and sharing it would refetch three whole documents on every dialog open. */
export var packagedFormConfigDefaultQueryKey = function (recordType) {
    return ['form-config-packaged-default', recordType];
};
var usePackagedFormConfigDefault = function (settingKey) {
    var _a;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var recordType = recordTypeOfFormConfigKey(settingKey);
    var _b = useQuery(packagedFormConfigDefaultQueryKey(recordType !== null && recordType !== void 0 ? recordType : ''), function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, ky
                    .get(CZNA_MODULE_SETTING_DEFAULT_DRIFT(formConfigSettingKey(recordType)))
                    .json()];
        });
    }); }, {
        enabled: Boolean(recordType),
        // Packaged defaults are classpath resources — they change on deploy only.
        staleTime: 60 * 60 * 1000,
        cacheTime: 120 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    }), data = _b.data, isLoading = _b.isLoading;
    return {
        packagedDefault: (_a = data === null || data === void 0 ? void 0 : data.packagedDefault) !== null && _a !== void 0 ? _a : undefined,
        isLoading: isLoading,
    };
};
export default usePackagedFormConfigDefault;
